/** Dot / bracket path helpers with immutable writes (`lines.0.qty`, `lines[0].qty`). */

export type PathSegment = string | number;

const cache = new Map<string, PathSegment[]>();

/** Splits `a.b[0].c` / `a.b.0.c` into segments; numeric segments become numbers. */
export function parsePath(path: string): PathSegment[] {
  const hit = cache.get(path);
  if (hit) return hit;
  const segments: PathSegment[] = [];
  for (const part of path.replace(/\[(\w+)\]/g, '.$1').split('.')) {
    if (part === '') continue;
    segments.push(/^\d+$/.test(part) ? Number(part) : part);
  }
  cache.set(path, segments);
  return segments;
}

/** Joins segments into canonical dot notation (`lines.0.qty`). */
export function joinPath(segments: readonly PathSegment[]): string {
  return segments.map(String).join('.');
}

/** Canonical form of a path (bracket → dot). */
export function normalizePath(path: string): string {
  return joinPath(parsePath(path));
}

export function getPath(obj: unknown, path: string | readonly PathSegment[]): unknown {
  const segments = typeof path === 'string' ? parsePath(path) : path;
  let current: unknown = obj;
  for (const seg of segments) {
    if (current === null || current === undefined) return undefined;
    current = (current as Record<string | number, unknown>)[seg];
  }
  return current;
}

function cloneContainer(value: unknown, nextSeg: PathSegment): Record<string | number, unknown> | unknown[] {
  if (Array.isArray(value)) return value.slice();
  if (value !== null && typeof value === 'object') return { ...(value as Record<string, unknown>) };
  return typeof nextSeg === 'number' ? [] : {};
}

/** Returns a structurally-shared copy of `obj` with `path` set to `value`; creates intermediate objects / arrays. */
export function setPath<T>(obj: T, path: string | readonly PathSegment[], value: unknown): T {
  const segments = typeof path === 'string' ? parsePath(path) : path;
  if (segments.length === 0) return value as T;
  const root = cloneContainer(obj, segments[0]!);
  let cursor: Record<string | number, unknown> | unknown[] = root;
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i]!;
    if (i === segments.length - 1) {
      (cursor as Record<string | number, unknown>)[seg] = value;
    } else {
      const next = cloneContainer((cursor as Record<string | number, unknown>)[seg], segments[i + 1]!);
      (cursor as Record<string | number, unknown>)[seg] = next;
      cursor = next;
    }
  }
  return root as T;
}

/** Removes a key / index immutably; arrays are spliced. */
export function deletePath<T>(obj: T, path: string | readonly PathSegment[]): T {
  const segments = typeof path === 'string' ? parsePath(path) : path;
  if (segments.length === 0) return obj;
  const parentSegments = segments.slice(0, -1);
  const last = segments[segments.length - 1]!;
  const parent = getPath(obj, parentSegments);
  if (Array.isArray(parent)) {
    const next = parent.slice();
    next.splice(Number(last), 1);
    return setPath(obj, parentSegments, next);
  }
  if (parent && typeof parent === 'object') {
    const next = { ...(parent as Record<string, unknown>) };
    delete next[String(last)];
    return setPath(obj, parentSegments, next);
  }
  return obj;
}

/** True when `path` equals `prefix` or is nested under it. */
export function isSubPath(path: string, prefix: string): boolean {
  const p = normalizePath(path);
  const pre = normalizePath(prefix);
  return p === pre || p.startsWith(`${pre}.`);
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (a instanceof Date || b instanceof Date) return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a as object).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
  const kb = Object.keys(b as object).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (!deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  return true;
}

/** Humanizes a path for messages: `lines.0.qty` → "Lines 1 · Qty". */
export function humanizePath(path: string): string {
  const parts: string[] = [];
  for (const seg of parsePath(path)) {
    if (typeof seg === 'number') {
      const last = parts.length - 1;
      if (last >= 0) parts[last] = `${parts[last]} ${seg + 1}`;
      else parts.push(String(seg + 1));
      continue;
    }
    const words = seg.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
    parts.push(words.replace(/^\w/, (c) => c.toUpperCase()));
  }
  return parts.join(' · ');
}
